import { supabase } from '@/lib/supabase/client'
import { nanoid } from 'nanoid'

// Types live in a sibling module so they can be imported without dragging
// the Supabase client into test / worker contexts.
export type {
  LocalizedString,
  FieldType,
  SelectOption,
  ValidationRule,
  SubFieldDefinition,
  FieldDefinition,
  ConditionOperator,
  ComparisonCondition,
  AndCondition,
  OrCondition,
  NotCondition,
  ConditionalExpression,
  ConditionalAction,
  ConditionalRule,
  CalculatedField,
  FormDefinition,
  FormGroup,
  Form,
} from './form-builder-types'

import type {
  FieldType,
  FieldDefinition,
  FormDefinition,
} from './form-builder-types'

// ─── Migration: old {steps} format → new {fields} format ─────────────────────

function migrateStepsToFields(raw: Record<string, unknown>): FormDefinition {
  const steps = (raw.steps as Array<Record<string, unknown>> | undefined) ?? []
  const allOldFields = steps.flatMap((s) => (s.fields as Array<Record<string, unknown>>) ?? [])

  const fields: FieldDefinition[] = allOldFields.map((f) => {
    const typeConfig: Record<string, unknown> = {}

    if (f.options) {
      typeConfig.options = (f.options as Array<{ value: string; label: string }>).map((o, i) => ({
        option_key: o.value || nanoid(4),
        label: { fa: o.label || o.value || '' },
        display_order: i,
      }))
    }
    if (f.min !== undefined) typeConfig.min = f.min
    if (f.max !== undefined) typeConfig.max = f.max
    if (f.minLength !== undefined) typeConfig.min_length = f.minLength
    if (f.maxLength !== undefined) typeConfig.max_length = f.maxLength
    if (f.subFields) {
      typeConfig.sub_fields = (f.subFields as Array<Record<string, unknown>>).map((sf) => ({
        field_id: (sf.key as string) || nanoid(),
        field_key: (sf.key as string) || nanoid(6),
        field_type: (sf.type as string) || 'text',
        label: { fa: (sf.label as string) || '' },
        is_required: Boolean(sf.required),
        type_config: {},
      }))
    }

    const rawKey = (f.key as string) || nanoid(6)
    return {
      field_id: rawKey,
      field_key: rawKey,
      field_type: (f.type as FieldType) || 'text',
      label: { fa: (f.label as string) || '' },
      description: f.description ? { fa: f.description as string } : undefined,
      is_required: Boolean(f.required),
      validations: [],
      type_config: typeConfig,
    }
  })

  return { fields, conditional_rules: [], calculated_fields: [] }
}

function normalizeActions(rules: ConditionalRule[]): ConditionalRule[] {
  return rules.map((rule) => ({
    ...rule,
    actions: rule.actions.map((a) => {
      const raw = a as Record<string, unknown>
      if (raw.action_type !== undefined && raw.type === undefined) {
        return { ...a, type: raw.action_type as ConditionalAction['type'] }
      }
      return a
    }),
  }))
}

export function parseDefinition(raw: unknown): FormDefinition {
  if (!raw || typeof raw !== 'object') {
    return { fields: [], conditional_rules: [], calculated_fields: [] }
  }
  const r = raw as Record<string, unknown>
  // Old format had top-level `steps` array with inline fields
  if (Array.isArray(r.steps)) {
    return migrateStepsToFields(r)
  }
  // New format
  const rules = (r.conditional_rules as ConditionalRule[]) ?? []
  return {
    fields: (r.fields as FieldDefinition[]) ?? [],
    conditional_rules: normalizeActions(rules),
    calculated_fields: (r.calculated_fields as CalculatedField[]) ?? [],
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseTitle(v: unknown): string {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object') return (v as Record<string, string>).fa ?? ''
  return ''
}

// ─── API Functions ────────────────────────────────────────────────────────────

export async function fetchFormGroups(projectId: string): Promise<FormGroup[]> {
  const [groupsRes, formsRes] = await Promise.all([
    supabase
      .from('form_groups')
      .select('*')
      .eq('project_id', projectId)
      .eq('is_deleted', false)
      .order('display_order'),
    supabase
      .from('forms')
      .select('*')
      .eq('project_id', projectId)
      .eq('is_deleted', false)
      .order('display_order'),
  ])

  if (groupsRes.error) throw groupsRes.error
  if (formsRes.error) throw formsRes.error

  return (groupsRes.data ?? []).map((g) => ({
    id: g.id,
    title: parseTitle(g.title),
    description: parseTitle(g.description),
    icon: g.icon ?? 'folder',
    displayOrder: g.display_order,
    forms: (formsRes.data ?? [])
      .filter((f) => f.group_id === g.id)
      .map((f) => ({
        id: f.id,
        title: parseTitle(f.title),
        description: parseTitle(f.description),
        icon: f.icon ?? 'file-text',
        formKey: f.form_key,
        groupId: f.group_id,
        instanceType: f.instance_type as 'single' | 'repeatable',
        isPublished: f.is_published,
        requiredForFinalSubmit: f.required_for_final_submit,
        displayOrder: f.display_order,
        currentVersionId: f.current_version_id,
      })),
  }))
}

export async function createFormGroup(
  projectId: string,
  userId: string,
  data: { title: string; description?: string; icon?: string }
) {
  const { data: result, error } = await supabase
    .from('form_groups')
    .insert({
      project_id: projectId,
      created_by: userId,
      title: { fa: data.title },
      description: data.description ? { fa: data.description } : null,
      icon: data.icon ?? 'folder',
    })
    .select('id')
    .single()
  if (error) throw error
  return result
}

export async function updateFormGroup(
  groupId: string,
  userId: string,
  data: { title: string; description?: string; icon?: string }
) {
  const { error } = await supabase
    .from('form_groups')
    .update({
      title: { fa: data.title },
      description: data.description ? { fa: data.description } : null,
      icon: data.icon ?? 'folder',
      updated_by: userId,
    })
    .eq('id', groupId)
  if (error) throw error
}

export async function deleteFormGroup(groupId: string, userId: string) {
  const { error } = await supabase
    .from('form_groups')
    .update({ is_deleted: true, deleted_at: new Date().toISOString(), updated_by: userId })
    .eq('id', groupId)
  if (error) throw error
}

export async function createForm(
  projectId: string,
  groupId: string,
  userId: string,
  data: { title: string; instanceType: 'single' | 'repeatable'; icon?: string }
) {
  const formKey =
    data.title
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '') || `form_${Date.now()}`

  const { data: result, error } = await supabase
    .from('forms')
    .insert({
      project_id: projectId,
      group_id: groupId,
      created_by: userId,
      title: { fa: data.title },
      form_key: formKey,
      instance_type: data.instanceType,
      icon: data.icon ?? 'file-text',
    })
    .select('id')
    .single()
  if (error) throw error
  return result
}

export async function deleteForm(formId: string, userId: string) {
  const { error } = await supabase
    .from('forms')
    .update({ is_deleted: true, deleted_at: new Date().toISOString(), updated_by: userId })
    .eq('id', formId)
  if (error) throw error
}

export async function fetchFormVersion(formId: string) {
  const { data: form, error: formErr } = await supabase
    .from('forms')
    .select('current_version_id, title, form_key, instance_type')
    .eq('id', formId)
    .single()
  if (formErr) throw formErr

  const { data: draft } = await supabase
    .from('form_drafts')
    .select('id, draft_definition, version, updated_at, updated_by')
    .eq('form_id', formId)
    .maybeSingle()

  if (draft) {
    // Also load published version's field keys so editor can lock them
    let publishedFieldKeys: Set<string> = new Set()
    let publishedVersionLabel: string | null = null
    let publishedDef: FormDefinition | null = null
    if (form.current_version_id) {
      const { data: pubVer } = await supabase
        .from('form_versions')
        .select('definition_snapshot, version_major, version_minor')
        .eq('id', form.current_version_id)
        .single()
      if (pubVer) {
        publishedDef = parseDefinition(pubVer.definition_snapshot)
        publishedFieldKeys = new Set(publishedDef.fields.map((f) => f.field_key))
        publishedVersionLabel = `${pubVer.version_major}.${pubVer.version_minor}`
      }
    }
    return {
      draftId: draft.id,
      draftVersion: draft.version,
      draftUpdatedAt: draft.updated_at,
      definition: parseDefinition(draft.draft_definition),
      publishedDef,
      publishedFieldKeys,
      publishedVersionLabel,
      formTitle: parseTitle(form.title),
      formKey: form.form_key,
      instanceType: form.instance_type,
      currentVersionId: form.current_version_id,
      isDraft: true,
    }
  }

  if (form.current_version_id) {
    const { data: version } = await supabase
      .from('form_versions')
      .select('id, definition_snapshot, version_major, version_minor')
      .eq('id', form.current_version_id)
      .single()
    if (version) {
      return {
        draftId: null,
        draftVersion: null,
        draftUpdatedAt: null,
        definition: parseDefinition(version.definition_snapshot),
        formTitle: parseTitle(form.title),
        formKey: form.form_key,
        instanceType: form.instance_type,
        currentVersionId: form.current_version_id,
        publishedVersionLabel: `${version.version_major}.${version.version_minor}`,
        isDraft: false,
      }
    }
  }

  return {
    draftId: null,
    draftVersion: null,
    draftUpdatedAt: null,
    definition: { fields: [], conditional_rules: [], calculated_fields: [] } as FormDefinition,
    formTitle: parseTitle(form.title),
    formKey: form.form_key,
    instanceType: form.instance_type,
    currentVersionId: form.current_version_id,
    isDraft: false,
  }
}

export class DraftConflictError extends Error {
  constructor() {
    super('تعارض پیش‌نویس: کاربر دیگری پیش‌نویس را ویرایش کرده است. لطفاً صفحه را بارگذاری کنید.')
    this.name = 'DraftConflictError'
  }
}

export async function saveFormDraft(
  formId: string,
  userId: string,
  definition: FormDefinition,
  draftId?: string | null,
  expectedVersion?: number
): Promise<{ id: string; version: number }> {
  if (draftId && expectedVersion !== undefined) {
    // Optimistic lock: only update if version matches; DB trigger auto-increments version
    const { data, error } = await supabase
      .from('form_drafts')
      .update({ draft_definition: definition, updated_by: userId })
      .eq('id', draftId)
      .eq('version', expectedVersion)
      .select('id, version')
      .maybeSingle()
    if (error) throw error
    if (!data) throw new DraftConflictError()
    return { id: data.id, version: data.version }
  } else {
    const { data, error } = await supabase
      .from('form_drafts')
      .upsert(
        { form_id: formId, updated_by: userId, draft_definition: definition },
        { onConflict: 'form_id' }
      )
      .select('id, version')
      .single()
    if (error) throw error
    return { id: data.id, version: data.version }
  }
}

// ─── Field factory ────────────────────────────────────────────────────────────

export function makeNewField(fieldType: FieldType): FieldDefinition {
  const id = nanoid()
  const key = `${fieldType}_${nanoid(8).toLowerCase().replace(/-/g, '_')}`
  const typeConfig: Record<string, unknown> = {}
  if (fieldType === 'single_select' || fieldType === 'multi_select') {
    typeConfig.options = []
  }
  if (fieldType === 'repeatable_array') {
    typeConfig.sub_fields = []
  }
  return {
    field_id: id,
    field_key: key,
    field_type: fieldType,
    label: { fa: '' },
    is_required: false,
    validations: [],
    type_config: typeConfig,
  }
}

// ─── Util: get all field keys from a definition ───────────────────────────────

export function getFieldsFlat(definition: FormDefinition): FieldDefinition[] {
  return definition.fields
}

// ─── Phase 6: per-change impact analysis ─────────────────────────────────────

export interface ImpactResult {
  field_key: string
  change_type: string
  affected_count: number
  approved_count: number
  sample_record_ids: string[]
  auto_convertible_count: number
  dirty_count: number
}

// ─── Publish form version (Phase 7 Edge Function) ────────────────────────────

export interface PublishDecision {
  diffItemId:         string
  change_type:        string
  field_key?:         string
  choice:             'grandfather' | 'migrate' | 'mark_for_revision'
  removed_option_key?: string
  old_type?:          string
  new_type?:          string
  migration_mapping?: Record<string, string>
}

export interface PublishVersionResult {
  success:       true
  version_id:    string
  version_major: number
  version_minor: number
  bump_type:     'minor' | 'major'
}

export async function publishFormVersion(params: {
  form_id:             string
  draft_version:       number
  client_diff:         Array<{ diffItemId: string; classification: string }>
  decisions:           PublishDecision[]
  super_user_confirm?: boolean
}): Promise<PublishVersionResult> {
  const { data, error } = await supabase.functions.invoke('publish-form-version', {
    body: params,
  })
  if (error) throw new Error(error.message)
  if (data && typeof data === 'object' && 'error' in data) {
    const d = data as { error: string; details?: unknown }
    throw new Error(d.error + (d.details ? `: ${JSON.stringify(d.details)}` : ''))
  }
  return data as PublishVersionResult
}

export async function analyzeFormChangeImpact(
  formId: string,
  changes: Array<{
    field_key: string
    change_type: string
    removed_option_key?: string
    old_type?: string
    new_type?: string
  }>
): Promise<ImpactResult[]> {
  const { data, error } = await supabase.rpc('analyze_form_change_impact', {
    p_form_id: formId,
    p_changes: changes,
  })
  if (error) throw error
  return (data as ImpactResult[]) ?? []
}

// ─── Version history ─────────────────────────────────────────────────────────

export interface FormVersionRow {
  id: string
  version_major: number
  version_minor: number
  bump_type: string
  published_at: string
  published_by: string
  published_by_name: string
  change_summary: Array<{ diffItemId: string; classification: string; type: string; field_key: string | null }> | null
  submission_count: number
  is_deprecated_manual: boolean
  is_current: boolean
  /** True when EITHER manual flag is set OR a later major-bump exists. */
  is_deprecated_effective: boolean
}

export async function fetchFormVersionHistory(formId: string): Promise<FormVersionRow[]> {
  // `submissions` has two FKs pointing at `form_versions` (`form_version_id`
  // and `legacy_from_version_id`), so PostgREST refuses to auto-pick one. We
  // hint the FK column name explicitly. The count reflects submissions
  // currently *pinned* to each version, not legacy upgrades from it.
  const [versionsRes, formRes] = await Promise.all([
    supabase
      .from('form_versions')
      .select(`
        id, version_major, version_minor, bump_type, published_at, published_by, change_summary,
        is_deprecated_manual,
        published_by_profile:user_profiles!form_versions_published_by_fkey(full_name),
        submissions:submissions!form_version_id(count)
      `)
      .eq('form_id', formId)
      .order('published_at', { ascending: false }),
    supabase.from('forms').select('current_version_id').eq('id', formId).single(),
  ])
  if (versionsRes.error) throw versionsRes.error
  const currentVersionId = formRes.data?.current_version_id ?? null
  const rows = (versionsRes.data ?? []).map((r: Record<string, unknown>) => ({
    id:               r.id as string,
    version_major:    r.version_major as number,
    version_minor:    r.version_minor as number,
    bump_type:        r.bump_type as string,
    published_at:     r.published_at as string,
    published_by:     r.published_by as string,
    published_by_name: (r.published_by_profile as { full_name?: string } | null)?.full_name ?? '—',
    change_summary:   r.change_summary as FormVersionRow['change_summary'],
    submission_count: ((r.submissions as Array<{ count: number }> | null)?.[0]?.count) ?? 0,
    is_deprecated_manual: (r.is_deprecated_manual as boolean | undefined) ?? false,
    is_current:           r.id === currentVersionId,
  }))
  // Auto-deprecated = a later version exists with bump_type='major'.
  // Computed in JS so the API stays one round-trip.
  return rows.map((row, idx) => {
    const hasLaterMajor = rows.some((other, otherIdx) =>
      otherIdx < idx && other.bump_type === 'major' // earlier idx = newer (we order desc)
    )
    return {
      ...row,
      is_current: row.is_current,
      is_deprecated_effective: row.is_deprecated_manual || hasLaterMajor,
    }
  })
}

/**
 * Toggle the manual `is_deprecated_manual` flag on a form version. Backend
 * refuses if the target is the current published version of its form.
 * Admin / super only.
 */
export async function setFormVersionDeprecationManual(
  versionId: string,
  deprecated: boolean,
): Promise<void> {
  const { data, error } = await supabase.rpc('set_form_version_deprecation_manual', {
    p_version_id: versionId,
    p_deprecated: deprecated,
  })
  if (error) throw error
  const result = data as { success?: boolean; error?: string }
  if (result?.error === 'forbidden') throw new Error('دسترسی غیرمجاز')
  if (result?.error === 'cannot_deprecate_current')
    throw new Error('نسخه فعلی فرم را نمی‌توان منسوخ کرد')
  if (result?.error === 'version_not_found') throw new Error('نسخه یافت نشد')
  if (result?.error) throw new Error(result.error)
}
